import { PHQ9_GAD7_SECTIONS } from './questionnaireLayout'
import { KeyedOption, Option } from './historyOptions'
import { getIn } from './eventLogger/valuePath'
import { Section, checks, choice, codes, date, rows, showWhen, text, value, withHint, yesno } from './historyLayoutKit'

// Wording is Standard's `participantHistory/MonthlySummaryNoteFields.tsx` and `participantHistoryForm/form/*`.

// The label "Other" is stored as `OTHER`; every other value is the label upper-cased.
const options = (...labels: string[]): Option[] =>
  labels.map((label) => ({ value: label.toUpperCase().replace(/[ -]/g, '_'), label }))

const REPORTS = (no: string, yes: string): Option[] => [
  { value: 'reports_no', label: no },
  { value: 'reports_yes', label: yes },
]

type Hints = Record<string, { hint?: string; placeholder?: string }>

const hinted = (sections: Section[], hints: Hints): Section[] =>
  sections.map((section) => ({
    ...section,
    fields: section.fields.map((field) => (hints[field.label] ? withHint(field, hints[field.label]) : field)),
  }))

const PROGRAM_INTERVENTIONS: KeyedOption[] = [
  { key: 'PHQ9_GAD7', label: 'PHQ9 and GAD7 offered biweekly to assess for progress.' },
  { key: 'BIOFEEDBACK', label: 'Utilize heart rate device for biofeedback breathing practices.' },
  { key: 'IN_APP_CHAT', label: 'Engage in daily in-app chat (Mon–Fri) with a licensed therapist/psychologist.' },
  { key: 'CBT_MINDFULLNESS_ACT', label: 'CBT, Mindfulness, and ACT practices offered daily.' },
  {
    key: 'PSYCHOEDUCATION',
    label: 'Psychoeducation on cognitive and emotional health topics including sleep and nutrition.',
  },
  {
    key: 'TELEHEALTH_VIDEO',
    label:
      'Optional telehealth video sessions with licensed provider offered within 10 business days of last appointment.',
  },
  { key: 'PEER_SUPPORT', label: 'Access to an anonymous, confidential peer support space.' },
]

const SERVICE_DELIVERY: KeyedOption[] = [
  { key: 'CONFIRM_SERVICE_PROVIDED_VIA_TELEHEALTH', label: 'Service provided via telehealth' },
  {
    key: 'CONFIRM_SERVICE_PROVIDED_VIA_HIPAA_COMPLIANT_SOFTWARE',
    label: 'Service provided via HIPAA compliant software',
  },
  { key: 'CONFIRM_INTAKE_TIME_AND_LOCATION', label: 'Participant confirmed location at time of intake' },
]

const MONTHLY_FUNCTIONAL_IMPAIRMENTS: KeyedOption[] = [
  { key: 'DIFFICULTY_MEETING_EXPECTATIONS', label: 'Difficulty meeting work/school/family expectations' },
  { key: 'STRUGGLE_WITH_RELATIONSHIPS', label: 'Struggle with relationships' },
  { key: 'NEGLECT_OF_PERSONAL_NEEDS', label: 'Neglect of personal needs' },
  { key: 'DIFFICULTY_IN_SLEEP', label: 'Difficulty in sleep' },
  { key: 'CHANGE_IN_APPETITE_OR_WEIGHT', label: 'Change in appetite or weight' },
  { key: 'IMPAIRED_COGNITIVE_FUNCTIONING', label: 'Impaired cognitive functioning' },
  { key: 'VOLATILE_MOOD_RESPONSES', label: 'Volatile mood responses' },
  { key: 'DIFFICULTY_MANAGING_TASKS', label: 'Difficulty managing tasks' },
  { key: 'LOSS_OF_INTEREST', label: 'Loss of interest' },
]

const MONTHLY_ASSESSMENT: KeyedOption[] = [
  { key: 'NO_NOTABLE_CHANGE', label: 'No notable change in symptoms over past 30 days' },
  { key: 'OVERALL_IMPROVEMENT', label: 'Overall symptom improvement' },
  { key: 'OVERALL_WORSENING', label: 'Overall symptom worsening' },
  { key: 'FLUCTUATING_PATTERN', label: 'Fluctuating symptom pattern' },
  { key: 'NOT_COMPLETED_QUESTIONNAIRES', label: 'Participant has not completed health questionnaires' },
  { key: 'REMINDERS_SENT', label: 'Provider has sent reminders and encouragement to complete' },
]

const MONTHLY_SUMMARY_NOTE_FIELDS: Section[] = [
  {
    title: 'Participant',
    fields: [
      value('First Name', 'participant.firstName'),
      value('Last Name', 'participant.lastName'),
      value('Insurance member ID', 'participant.memberID'),
    ],
  },
  {
    title: 'Assessing Practitioner',
    fields: [value('Name', 'assessingPractitioner.name'), value('License Type', 'assessingPractitioner.licenseType')],
  },
  // The server sends a CPT code only for Kaiser patients, which is when Standard shows the field too.
  {
    title: 'CPT Code',
    fields: [showWhen((entry) => entry.cptCode !== undefined, value('Monthly Case Rate Code', 'cptCode'))],
  },
  {
    title: 'Dates of Service',
    fields: [date('Start Date', 'datesOfService.startDate'), date('End Date', 'datesOfService.endDate')],
  },
  { title: 'Treatment Goals', fields: [text('SMART goals', 'treatmentPlan.smartGoals')] },
  { title: 'Billing and Diagnosis Codes', fields: [codes('', 'billingCodes')] },
  { title: 'Interventions', fields: [checks('', 'interventions', PROGRAM_INTERVENTIONS)] },
  {
    title: 'Functional Impairments',
    fields: [checks('', 'reasonForSeekingTreatment.functionalImpairments', MONTHLY_FUNCTIONAL_IMPAIRMENTS, 'Other')],
  },
  {
    title: 'Participant Engagement',
    fields: [
      text('Participation', 'participantEngagement.participation'),
      text('Chat Summary', 'participantEngagement.chatSummary'),
    ],
  },
  ...PHQ9_GAD7_SECTIONS('progressAssessment.sinceLastAssessment.healthQuestionnaire'),
  {
    title: 'Since Last Assessment',
    fields: [
      checks(
        'Assessment',
        'progressAssessment.sinceLastAssessment.assessment',
        MONTHLY_ASSESSMENT.filter(({ key }) => key !== 'REMINDERS_SENT')
      ),
      showWhen(
        (entry) =>
          getIn(entry, 'progressAssessment.sinceLastAssessment.assessment.NOT_COMPLETED_QUESTIONNAIRES') === true,
        yesno(
          'Provider has sent reminders and encouragement to complete',
          'progressAssessment.sinceLastAssessment.assessment.REMINDERS_SENT'
        )
      ),
      showWhen(
        (entry) =>
          getIn(entry, 'progressAssessment.sinceLastAssessment.assessment.OVERALL_WORSENING') === true ||
          getIn(entry, 'progressAssessment.sinceLastAssessment.assessment.FLUCTUATING_PATTERN') === true,
        text('Details', 'progressAssessment.sinceLastAssessment.assessment.details')
      ),
    ],
  },
  {
    title: 'Risk Assessment',
    fields: [
      choice('', 'progressAssessment.riskAssessment.status', [
        { value: 'noChanges', label: 'No known changes to risk level since last encounter' },
        { value: 'changes', label: 'Changes to risk level' },
      ]),
      showWhen(
        (entry) => getIn(entry, 'progressAssessment.riskAssessment.status') === 'changes',
        text('Details', 'progressAssessment.riskAssessment.details')
      ),
    ],
  },
  {
    title: 'Participant Response',
    fields: [
      choice('', 'progressAssessment.participantResponse.response', [
        {
          value: 'activelyEngaged',
          label: "Participant is actively engaged in program content and therapist's interventions",
        },
        {
          value: 'minimallyEngaged',
          label: 'Participant is minimally engaged but reports no other interventions needed at this time',
        },
        { value: 'other', label: 'Other (specify engagement level)' },
      ]),
      showWhen(
        (entry) => getIn(entry, 'progressAssessment.participantResponse.response') === 'other',
        text('Details', 'progressAssessment.participantResponse.details')
      ),
    ],
  },
  {
    title: 'Plan',
    fields: [
      choice('', 'progressAssessment.plan.planType', [
        {
          value: 'continueSupport',
          label: 'Therapist will continue to support client via app chat and offer check in calls as needed',
        },
        { value: 'other', label: 'Other' },
      ]),
      showWhen(
        (entry) => getIn(entry, 'progressAssessment.plan.planType') === 'other',
        text('Details', 'progressAssessment.plan.details')
      ),
    ],
  },
]

const TREATMENT_PLAN_COMPREHENSIVE_FIELDS: Section[] = [
  {
    title: 'Overall Information',
    fields: [
      value('First name', 'FIRST_NAME'),
      value('Last name', 'LAST_NAME'),
      value('Kaiser MRN', 'MEMBER_ID'),
      value('Provider name', 'PROVIDER_NAME'),
      value('License type', 'LICENSE_TYPE'),
    ],
  },
  {
    title: 'Treatment Plan',
    fields: [text('Presenting Problem', 'PRESENTING_PROBLEM'), text('Long Term Goal', 'LONG_TERM_GOAL')],
  },
  {
    title: 'Short Term Goal',
    fields: [
      text('S - Specific (clear, concrete, focused)', 'SHORT_TERM_GOAL.SPECIFIC'),
      text('M - Measurable (tracked/quantified objectively)', 'SHORT_TERM_GOAL.MEASURABLE'),
      text('A - Achievable (realistic for participant)', 'SHORT_TERM_GOAL.ACHIEVABLE'),
      text('R - Relevant (clearly connected to diagnosis and reason for treatment)', 'SHORT_TERM_GOAL.RELEVANT'),
      text('T - Time-bound (specific timeline)', 'SHORT_TERM_GOAL.TIME_BOUND'),
      text('SMART Goal Statement', 'SHORT_TERM_GOAL.GOAL_STATEMENT'),
    ],
  },
  { title: 'Billing and Diagnosis Codes', fields: [codes('', 'BILLING_CODES')] },
  {
    title: 'Interventions and participant agreement',
    fields: [
      checks('', 'INTERVENTIONS_AND_PARTICIPANT_AGREEMENT', PROGRAM_INTERVENTIONS),
      text('Strengths', 'INTERVENTIONS_AND_PARTICIPANT_AGREEMENT.STRENGTHS'),
      text('Other recommendations and notes', 'INTERVENTIONS_AND_PARTICIPANT_AGREEMENT.OTHER'),

      yesno(
        'Participant agrees to the discussed treatment plan',
        'INTERVENTIONS_AND_PARTICIPANT_AGREEMENT.PARTICIPANT_AGREEMENT'
      ),
    ],
  },
]

const FUNCTIONAL_IMPAIRMENTS: KeyedOption[] = [
  {
    key: 'DIFFICULTY_MEETING_EXPECTATIONS',
    label: 'Difficulty meeting expectations at work or school ',
    hint: 'Absenteeism, frequent mistakes, inability to focus on tasks.',
  },
  {
    key: 'STRUGGLE_WITH_RELATIONSHIPS',
    label: 'Struggling with relationships',
    hint: 'Withdrawing from friends or family, conflict with peers, difficulty trusting others.',
  },
  {
    key: 'NEGLECT_OF_PERSONAL_NEEDS',
    label: 'Neglect of personal grooming, nutrition or medication routines',
    hint: 'Poor bathing habits, skipping meals, missing prescribed doses of medication.',
  },
  {
    key: 'DIFFICULTY_IN_SLEEP',
    label: 'Insomnia, hypersomnia or irregular sleep patterns',
    hint: 'Difficulty falling or staying asleep, excessive daytime sleepiness.',
  },
  {
    key: 'CHANGE_IN_APPETITE_OR_WEIGHT',
    label: 'Marked increase or decrease in appetite or body weight',
    hint: 'Binge-eating episodes, loss of appetite, significant unintentional weight loss/gain.',
  },
  {
    key: 'IMPAIRED_COGNITIVE_FUNCTIONING',
    label: 'Impaired cognitive functioning',
    hint: 'Trouble staying on task, forgetfulness, difficulty processing information.',
  },
  {
    key: 'VOLATILE_MOOD_RESPONSES',
    label: 'Volatile or blunted mood responses',
    hint: 'Frequent irritability or anger outbursts, emotional numbness, difficulty calming down.',
  },
  {
    key: 'DIFFICULTY_MANAGING_TASKS',
    label: 'Challenges managing household or other tasks',
    hint: 'Forgetting to pay bills, inability to shop or prepare meals, neglecting chores.',
  },
  { key: 'LOSS_OF_INTEREST', label: 'Loss of interest or pleasure in hobbies' },
]

const PROTECTIVE_FACTORS: KeyedOption[] = [
  { key: 'STRONG_SOCIAL_SUPPORT', label: 'Strong social support' },
  { key: 'ENGAGEMENT_IN_THERAPY', label: 'Engagement in therapy' },
  { key: 'COPING_SKILLS', label: 'Coping skills' },
  { key: 'FUTURE_ORIENTED_THINKING', label: 'Future-oriented thinking' },
  { key: 'MOTIVITED_FOR_TREATMENT', label: 'Motivated for treatment' },
  { key: 'RELIGIOUS_BELIEFS', label: 'Religious/spiritual beliefs' },
  { key: 'OTHER', label: 'Other protective factors' },
]

const NEXT_STEPS: KeyedOption[] = [
  { key: 'SAFETY_PLAN_REVIEWED_EMAILLED', label: 'Safety plan developed, reviewed and emailed to client.' },
  { key: 'SAFETY_PLAN_DOCUMENTED_IN_RECORD', label: 'Safety plan documented in medical record.' },
  {
    key: 'LAW_ENFORCEMENT_OR_EMERGENCY_CONTACT_NOTIFIED',
    label: 'Law enforcement/emergency contact notified due to imminent risk.',
  },
  { key: 'NO_FURTHER_ASSISTANCE_NEEDED', label: 'Patient presents as low risk, no safety plan needed at this time.' },
]

const MEDICAL_HISTORY: KeyedOption[] = [
  ['ARTHRITIS', 'Arthritis'],
  ['ASTHMA', 'Asthma'],
  ['AUTOIMMUNE_DISORDER', 'Autoimmune disorder'],
  ['BACK_PROBLEMS', 'Back problems'],
  ['CANCER_CURRENT_OR_PAST', 'Cancer (current or past)'],
  ['CHRONIC_PAIN', 'Chronic pain'],
  ['COPD_EMPHYSEMA', 'COPD/emphysema'],
  ['DIABETES', 'Diabetes'],
  ['HEART_DISEASE', 'Heart disease'],
  ['HIGH_BLOOD_PRESSURE', 'High blood pressure'],
  ['HIGH_CHOLESTEROL', 'High cholesterol'],
  ['HORMONAL_IMBALANCE', 'Hormonal imbalance'],
  ['IBS_CROHNS_COLITIS', "IBS / Crohn's / Colitis"],
  ['LIVER_DISEASE', 'Liver disease'],
  ['MIGRAINES', 'Migraines'],
  ['SEIZURES', 'Seizures'],
  ['SLEEP_APNEA', 'Sleep apnea'],
  ['STROKE', 'Stroke'],
  ['THYROID_DISORDER', 'Thyroid disorder'],
  ['TRAUMATIC_BRAIN_INJURY', 'Traumatic brain injury'],
  ['ULCERS', 'Ulcers'],
].map(([key, label]) => ({ key, label }))

const FAMILY_HISTORY_ISSUES: KeyedOption[] = [
  { key: 'TRAUMA_OR_PTSD', label: 'Trauma or PTSD' },
  { key: 'SUBSTANCE_ABUSE', label: 'Substance abuse' },
  { key: 'ANXIETY', label: 'Anxiety' },
  { key: 'SCHIZOPHRENIA', label: 'Schizophrenia' },
  { key: 'SUICIDE', label: 'Suicide' },
  { key: 'DEPRESSION', label: 'Depression' },
  { key: 'BIPOLAR_DISORDER', label: 'Bipolar disorder' },
  { key: 'ADHD', label: 'ADHD' },
  { key: 'INCARCERATION', label: 'Incarceration' },
]

const LANGUAGES = options('English', 'Spanish', 'French', 'German', 'Italian', 'Portuguese', 'Other').map(
  ({ label }) => ({ value: label, label })
)

const TAKES_MEDICATION = (entry: Record<string, unknown>) =>
  getIn(entry, 'BIOPYCHOSOCIAL_EVALUATION.MEDICATIONS') === 'reports_yes'

const SUBSTANCE_USE = options('None', 'Using', 'Have used')

const MSE = (key: string, label: string, ...labels: string[]) =>
  choice(label, `MENTAL_STATUS_EXAMINATION.${key}`, options(...labels))

const INTAKE_NOTE_COMPREHENSIVE_FIELDS: Section[] = [
  {
    title: 'Overall Information',
    fields: [
      value('First name', 'FIRST_NAME'),
      value('Last name', 'LAST_NAME'),
      value('Kaiser MRN', 'MEMBER_ID'),
      value('Call start time', 'CALL_START_TIME', 'datetime'),
      value('Call end time', 'CALL_END_TIME', 'datetime'),
      value('Provider name', 'PROVIDER_NAME'),
      value('License type', 'LICENSE_TYPE'),
    ],
  },
  {
    title: 'Service Delivery & Compliance',
    fields: [checks('', 'SERVICE_DELIVERY_AND_COMPLIANCE', SERVICE_DELIVERY)],
  },
  {
    title: 'Accessibility Needs',
    fields: [
      yesno('Non-English speaking', 'ACCESSIBILITY_NEEDS.NON_ENGLISH_SPEAKING'),
      showWhen(
        (entry) => getIn(entry, 'ACCESSIBILITY_NEEDS.NON_ENGLISH_SPEAKING') === true,
        choice('Language used', 'ACCESSIBILITY_NEEDS.LANGUAGE_USED', LANGUAGES)
      ),
      yesno(
        'Interpretive services were provided for this interview.',
        'ACCESSIBILITY_NEEDS.PROVIDE_INTERPRETIVE_SERVICES'
      ),
      choice(
        'Cultural Considerations',
        'ACCESSIBILITY_NEEDS.CULTURAL_CONSIDERATIONS',
        REPORTS('Participant reports no cultural considerations.', 'Participant reports cultural considerations.')
      ),
      showWhen(
        (entry) => getIn(entry, 'ACCESSIBILITY_NEEDS.CULTURAL_CONSIDERATIONS') === 'reports_yes',
        text('Cultural Considerations', 'ACCESSIBILITY_NEEDS.CULTURAL_CONSIDERATIONS_DETAILS')
      ),
      choice(
        'Access Issues',
        'ACCESSIBILITY_NEEDS.ACCESS_ISSUES',
        REPORTS('Participant reports no access issues.', 'Participant reports access issues.')
      ),
      showWhen(
        (entry) => getIn(entry, 'ACCESSIBILITY_NEEDS.ACCESS_ISSUES') === 'reports_yes',
        text('Access Issues', 'ACCESSIBILITY_NEEDS.ACCESS_ISSUES_DETAILS')
      ),
    ],
  },
  ...PHQ9_GAD7_SECTIONS('REASON_FOR_SEEKING_TREATMENT.HEALTH_QUESTIONNAIRE'),
  {
    title: 'Functional Impairments',
    fields: [
      checks(
        '',
        'REASON_FOR_SEEKING_TREATMENT.FUNCTIONAL_IMPAIRMENTS',
        FUNCTIONAL_IMPAIRMENTS,
        'Other functional impairments (if applicable)'
      ),
    ],
  },
  { title: 'Narrative', fields: [text('Narrative', 'REASON_FOR_SEEKING_TREATMENT.NARRATIVE')] },
  {
    title: 'Mental Health Treatment History',
    fields: [
      yesno(
        'Participant has seen a mental health professional in the past.',
        'MENTAL_HEALTH_TREATMENT_HISTORY.SEEN_MENTAL_HEALTH_PROFESSIONAL_IN_PAST'
      ),
      showWhen(
        (entry) => getIn(entry, 'MENTAL_HEALTH_TREATMENT_HISTORY.SEEN_MENTAL_HEALTH_PROFESSIONAL_IN_PAST') === true,
        text('Care details', 'MENTAL_HEALTH_TREATMENT_HISTORY.CARE_DETAILS')
      ),
      yesno(
        'Participant has been hospitalized for mental health reasons.',
        'MENTAL_HEALTH_TREATMENT_HISTORY.HOSPITALIZED_FOR_MENTAL_HEALTH'
      ),
      showWhen(
        (entry) => getIn(entry, 'MENTAL_HEALTH_TREATMENT_HISTORY.HOSPITALIZED_FOR_MENTAL_HEALTH') === true,
        text('Hospitalization details', 'MENTAL_HEALTH_TREATMENT_HISTORY.HOSPITALIZATION_DETAILS')
      ),
      choice(
        'Trauma',
        'MENTAL_HEALTH_TREATMENT_HISTORY.HISTORY_OF_TRAUMA',
        REPORTS(
          'Participant reports not having history of or exposure to trauma.',
          'Participant reports having history of or exposure to trauma.'
        )
      ),
      showWhen(
        (entry) => getIn(entry, 'MENTAL_HEALTH_TREATMENT_HISTORY.HISTORY_OF_TRAUMA') === 'reports_yes',
        text('Trauma details', 'MENTAL_HEALTH_TREATMENT_HISTORY.TRAUMA_DETAILS')
      ),
    ],
  },
  {
    title: 'Risk Assessment',
    fields: [
      yesno('Participant denies current or past SI, SIB, SA, HI', 'RISK_ASSESSMENT.BASE.DENY_SI_SIB_SA_HI'),
      yesno(
        'Participant endorses current SI with intent and plan. Crisis intervention initiated.',
        'RISK_ASSESSMENT.BASE.SI_WITH_INTENT_PLAN_CRISIS_INITIATED'
      ),
      yesno('Other', 'RISK_ASSESSMENT.BASE.OTHER'),
      showWhen(
        (entry) => getIn(entry, 'RISK_ASSESSMENT.BASE.OTHER') === true,
        text('Other details', 'RISK_ASSESSMENT.BASE.OTHER_DETAILS')
      ),
    ],
  },
  {
    title: 'Protective Factors Identified',
    fields: [
      checks('', 'RISK_ASSESSMENT.PROTECTIVE_FACTORS_IDENTIFIED', PROTECTIVE_FACTORS),
      showWhen(
        (entry) => getIn(entry, 'RISK_ASSESSMENT.PROTECTIVE_FACTORS_IDENTIFIED.OTHER') === true,
        text('Other protective factors', 'RISK_ASSESSMENT.PROTECTIVE_FACTORS_IDENTIFIED.OTHER_FACTOR')
      ),
    ],
  },
  {
    title: 'Next Steps',
    fields: [
      checks('', 'RISK_ASSESSMENT.NEXT_STEPS', NEXT_STEPS),
      text('Next steps details', 'RISK_ASSESSMENT.NEXT_STEPS.NEXT_STEPS_DETAILS'),
    ],
  },
  {
    title: 'Medical History',
    fields: [
      checks('', 'BIOPYCHOSOCIAL_EVALUATION.MEDICAL_HISTORY', MEDICAL_HISTORY),
      choice(
        'Allergies',
        'BIOPYCHOSOCIAL_EVALUATION.ALLERGIES',
        REPORTS('Participant reports no allergies.', 'Participant reports having allergies.')
      ),
      showWhen(
        (entry) => getIn(entry, 'BIOPYCHOSOCIAL_EVALUATION.ALLERGIES') === 'reports_yes',
        text('Allergies', 'BIOPYCHOSOCIAL_EVALUATION.ALLERGIES_DETAILS')
      ),
      choice(
        'Other Medical Conditions',
        'BIOPYCHOSOCIAL_EVALUATION.MEDICAL_CONDITIONS',
        REPORTS('Participant reports no other medical conditions.', 'Participant reports other medical conditions.')
      ),
      showWhen(
        (entry) => getIn(entry, 'BIOPYCHOSOCIAL_EVALUATION.MEDICAL_CONDITIONS') === 'reports_yes',
        text('Other conditions', 'BIOPYCHOSOCIAL_EVALUATION.MEDICAL_CONDITIONS_DETAILS')
      ),
      text('Additional notes (if applicable)', 'BIOPYCHOSOCIAL_EVALUATION.ADDITIONAL_NOTES'),
    ],
  },
  {
    title: 'Medications',
    fields: [
      choice(
        '',
        'BIOPYCHOSOCIAL_EVALUATION.MEDICATIONS',
        REPORTS(
          'Participant reports not taking any prescription medications.',
          'Participant reports taking prescription medications.'
        )
      ),
      showWhen(
        TAKES_MEDICATION,
        rows('Medication', 'BIOPYCHOSOCIAL_EVALUATION.MEDICATIONS_DETAILS', [
          { key: 'NAME', label: 'Name' },
          { key: 'DOSAGE', label: 'Dosage' },
          { key: 'FREQUENCY', label: 'Frequency' },
        ])
      ),
      showWhen(
        TAKES_MEDICATION,
        value('Primary Care Doctor — Name', 'BIOPYCHOSOCIAL_EVALUATION.PRIMARY_CARE_DOCTOR.NAME')
      ),
      showWhen(
        TAKES_MEDICATION,
        value('Primary Care Doctor — Phone', 'BIOPYCHOSOCIAL_EVALUATION.PRIMARY_CARE_DOCTOR.PHONE')
      ),
    ],
  },
  {
    title: 'Substance Abuse',
    fields: [
      choice('Tobacco (Cigarettes, chew, vape)', 'BIOPYCHOSOCIAL_EVALUATION.SUBSTANCE_ABUSE.TOBACCO', SUBSTANCE_USE),
      choice('Alcohol', 'BIOPYCHOSOCIAL_EVALUATION.SUBSTANCE_ABUSE.ALCOHOL', SUBSTANCE_USE),
      choice('Marijuana', 'BIOPYCHOSOCIAL_EVALUATION.SUBSTANCE_ABUSE.MARIJUANA', SUBSTANCE_USE),
      choice('Non-prescription drugs', 'BIOPYCHOSOCIAL_EVALUATION.SUBSTANCE_ABUSE.NON_PRESCRIBED_DRUGS', SUBSTANCE_USE),
      choice(
        'Other recreational substances',
        'BIOPYCHOSOCIAL_EVALUATION.SUBSTANCE_ABUSE.OTHER_RECREATIONAL_DRUGS',
        SUBSTANCE_USE
      ),
      yesno(
        'Participant reports interest in exploring options to reduce use.',
        'BIOPYCHOSOCIAL_EVALUATION.SUBSTANCE_ABUSE.REPORTS_INTEREST_TO_REDUCE_USE'
      ),
      yesno(
        'Participant reports currently receiving support for substance use.',
        'BIOPYCHOSOCIAL_EVALUATION.SUBSTANCE_ABUSE.REPORTS_RECEIVING_SUPPORT'
      ),
      showWhen(
        (entry) => getIn(entry, 'BIOPYCHOSOCIAL_EVALUATION.SUBSTANCE_ABUSE.REPORTS_RECEIVING_SUPPORT') === true,
        text('Support details', 'BIOPYCHOSOCIAL_EVALUATION.SUBSTANCE_ABUSE.SUPPORT_DETAILS')
      ),
    ],
  },
  {
    title: 'Family History',
    fields: [
      choice(
        '',
        'BIOPYCHOSOCIAL_EVALUATION.FAMILY_HISTORY',
        REPORTS(
          'Participant reports no family history of mental illness.',
          'Participant reports family history of mental illness.'
        )
      ),
      showWhen(
        (entry) => getIn(entry, 'BIOPYCHOSOCIAL_EVALUATION.FAMILY_HISTORY') === 'reports_yes',
        checks('Issues', 'BIOPYCHOSOCIAL_EVALUATION.FAMILY_HISTORY_ISSUES', FAMILY_HISTORY_ISSUES)
      ),
      showWhen(
        (entry) => getIn(entry, 'BIOPYCHOSOCIAL_EVALUATION.FAMILY_HISTORY') === 'reports_yes',
        text('Family history details (if applicable)', 'BIOPYCHOSOCIAL_EVALUATION.FAMILY_HISTORY_DETAILS')
      ),
    ],
  },
  {
    title: 'Current Living Situation',
    fields: [text('Current living situation', 'BIOPYCHOSOCIAL_EVALUATION.CURRENT_LIVING_SITUATION')],
  },
  {
    title: 'Mental Status Examination — Observations',
    fields: [
      MSE('APPEARANCE', 'Appearance', 'Neat', 'Disheveled', 'Inappropriate', 'Bizarre', 'Other'),
      MSE('SPEECH', 'Speech', 'Normal', 'Tangential', 'Pressured', 'Impoverished', 'Other'),
      MSE('EYE_CONTACT', 'Eye contact', 'Normal', 'Intense', 'Avoidant', 'Other'),
      MSE('MOTOR_ACTIVITY', 'Motor activity', 'Normal', 'Restless', 'Tics', 'Slowed', 'Other'),
      MSE('AFFECT', 'Affect', 'Full', 'Constrained', 'Flat', 'Labile', 'Other'),
    ],
  },
  {
    title: 'Mental Status Examination — Mood',
    fields: [MSE('MOOD', '', 'Euthymic', 'Anxious', 'Angry', 'Depressed', 'Euphoric', 'Irritable', 'Other')],
  },
  {
    title: 'Mental Status Examination — Cognition',
    fields: [
      MSE('ORIENTATION_IMPAIRMENT', 'Orientation impairment', 'None', 'Place', 'Object', 'Person', 'Time'),
      MSE('MEMORY_IMPAIRMENT', 'Memory impairment', 'None', 'Short term', 'Long term', 'Other'),
      MSE('ATTENTION', 'Attention', 'Normal', 'Distracted', 'Other'),
    ],
  },
  {
    title: 'Mental Status Examination — Perception',
    fields: [
      MSE('HALLUCINATIONS', 'Hallucinations', 'None', 'Auditory', 'Visual', 'Other'),
      MSE('DELUSIONS', 'Delusions', 'None', 'Grandiose', 'Paranoid', 'Religious', 'Other'),
    ],
  },
  {
    title: 'Mental Status Examination — Behavior',
    fields: [
      MSE(
        'BEHAVIOR',
        '',
        'Cooperative',
        'Guarded',
        'Hyperactive',
        'Agitated',
        'Paranoid',
        'Stereotyped',
        'Aggressive',
        'Bizarre',
        'Withdrawn',
        'Other'
      ),
    ],
  },
  {
    title: 'Mental Status Examination — Insight / Judgment',
    fields: [MSE('INSIGHT_JUDGMENT', '', 'Good', 'Fair', 'Inappropriate')],
  },
  {
    title: 'Mental Status Examination',
    fields: [text('Additional notes', 'MENTAL_STATUS_EXAMINATION.ADDITIONAL_NOTES')],
  },
  {
    title: 'Diagnostic Summary and Case Conceptualization',
    fields: [
      codes('Billing codes', 'BILLING_CODE'),
      text('Clinical formulation', 'CLINICAL_FORMULATION'),
      text('Case conceptualization', 'CASE_CONCEPTUALIZATION'),
    ],
  },
  {
    title: 'Confirmation and Next Steps',
    fields: [
      yesno('Participant is eligible for program enrollment.', 'ELIGIBILITY.ELIGIBLE_FOR_PROGRAM_ENROLLMENT'),
      yesno('Participant declines program enrollment.', 'ELIGIBILITY.PARTICIPANT_DECLINES_ENROLLMENT'),
      yesno(
        'Participant is not eligible for program enrollment due to clinical appropriateness.',
        'ELIGIBILITY.NOT_ELIGIBLE_FOR_PROGRAM_ENROLLMENT'
      ),
      yesno('Reviewed confidentiality and treatment expectations.', 'REVIEWED_CONFIDENTIALITY_AND_EXPECTATIONS'),
    ],
  },
]

const ADVANCED_FOLLOW_UP_NOTE_FIELDS: Section[] = [
  {
    title: 'S — Subjective',
    fields: [
      text("Chief Concern / Reason for Visit (patient's own words)", 'SUBJECTIVE.CHIEF_CONCERN'),
      text('Updates since last visit', 'SUBJECTIVE.UPDATES_SINCE_LAST_VISIT'),
      text('Mental Health Status (self-reported)', 'SUBJECTIVE.MENTAL_HEALTH_STATUS'),
      text('Physical Symptoms / Side Effects', 'SUBJECTIVE.PHYSICAL_SYMPTOMS'),
      text('Medication/Supplement Updates', 'SUBJECTIVE.MEDICATION_SUPPLEMENT_UPDATES'),
      text('Safety Concerns', 'SUBJECTIVE.SAFETY_CONCERNS'),
    ],
  },
  {
    title: 'O — Objective',
    fields: [
      text('Relevant Metrics/Labs (if reviewed)', 'OBJECTIVE.RELEVANT_METRICS_OR_LABS'),
      text(
        'Behavioral Observations/Other Pertinent Findings',
        'OBJECTIVE.BEHAVIORAL_OBSERVATIONS_OR_OTHER_PERTINENT_FINDINGS'
      ),
    ],
  },
  {
    title: 'A — Assessment',
    fields: [text('Clinical summary of how the patient is progressing', 'ASSESSMENT.CLINICAL_SUMMARY')],
  },
  {
    title: 'P — Plan',
    fields: [
      text('Focus areas until next visit', 'PLAN.FOCUS_AREAS'),
      text('Lifestyle Goals (SMART format if possible)', 'PLAN.LIFESTYLE_GOALS'),
      text('Nutrition / Metabolic Intervention Adjustments', 'PLAN.NUTRITION_METABOLIC_ADJUSTMENTS'),
      text('Mental Health / Somatic Tools', 'PLAN.MENTAL_HEALTH_SOMATIC_TOOLS'),
      text('Medications / Supplements', 'PLAN.MEDICATIONS_SUPPLEMENTS'),
      text('Referrals or escalation needs', 'PLAN.REFERRALS_ESCALATION'),
      text('Next Scheduled Session', 'PLAN.NEXT_SCHEDULED_SESSION'),
      yesno('Patient expressed understanding? Yes', 'PLAN.PATIENT_EXPRESSED_UNDERSTANDING'),
      yesno('Patient agrees with plan? Yes', 'PLAN.PATIENT_AGREES_WITH_PLAN'),
    ],
  },
]

export const MONTHLY_SUMMARY_NOTE_SECTIONS = hinted(MONTHLY_SUMMARY_NOTE_FIELDS, {
  Participation: { placeholder: 'List of completed practices for the month broken down by type' },
  'Chat Summary': { placeholder: 'Briefly summarize overall themes from chat communications' },
})

export const TREATMENT_PLAN_COMPREHENSIVE_SECTIONS = hinted(TREATMENT_PLAN_COMPREHENSIVE_FIELDS, {
  'Presenting Problem': { placeholder: 'Reason for seeking care or reason for updating treatment plan.' },
  'Long Term Goal': { placeholder: "Please describe the participant's long-term treatment goal." },
  'S - Specific (clear, concrete, focused)': {
    placeholder:
      'Be precise: What will the patient do differently? What behavior, coping skill, or specific change in functioning are you targeting?',
  },
  'M - Measurable (tracked/quantified objectively)': {
    placeholder:
      'How will success be measured - Frequency of behavior, rating scales, percentages, days of the week, etc. It MUST be objective and not a feeling.',
  },
  'R - Relevant (clearly connected to diagnosis and reason for treatment)': {
    placeholder:
      "Connect the goal directly to the patient's diagnosis or symptoms (e.g., anxiety, depression, sleep disturbance, or functional challenges).",
  },
  'T - Time-bound (specific timeline)': {
    placeholder: 'Include a specific timeframe for the goal. How long will it take to achieve the goal?',
  },
  'SMART Goal Statement': {
    placeholder:
      'Write the finished SMART goal here, blending all of the above pieces into a clear and complete sentence.',
  },
})

export const INTAKE_NOTE_COMPREHENSIVE_SECTIONS = hinted(INTAKE_NOTE_COMPREHENSIVE_FIELDS, {
  'Participant has seen a mental health professional in the past.': { hint: 'Box checked if participant endorses.' },
  Narrative: { hint: 'Presenting problems and current symptoms.' },
  Other: { hint: 'E.g., history of SI/SA/HI; current SI without plan/intent - details documented below.' },
  'Next steps details': { hint: 'Include the safety plan (if applicable) or other next steps.' },
})

export const ADVANCED_FOLLOW_UP_NOTE_SECTIONS = hinted(ADVANCED_FOLLOW_UP_NOTE_FIELDS, {
  "Chief Concern / Reason for Visit (patient's own words)": {
    placeholder: "Enter the patient's chief concern in their own words",
  },
  'Updates since last visit': { placeholder: 'Lab results, symptoms, milestones, concerns' },
  'Mental Health Status (self-reported)': { placeholder: 'Mood, anxiety, sleep quality, energy levels, stressors' },
  'Physical Symptoms / Side Effects': { placeholder: 'e.g., GI changes, dizziness, thirst, headaches, fatigue' },
  'Safety Concerns': { placeholder: 'Red flags, SI/HI, major functional decline — escalate immediately if present' },
  'Behavioral Observations/Other Pertinent Findings': {
    placeholder: 'Affect, engagement level, cognition, appearance',
  },
  'Clinical summary of how the patient is progressing': {
    placeholder: 'Include metabolic, psychiatric, behavioral, and lifestyle lenses.',
  },
  'Nutrition / Metabolic Intervention Adjustments': {
    placeholder:
      'Example areas: macronutrients, electrolytes, timing of meals, hydration, fiber titration, ketone monitoring',
  },
  'Mental Health / Somatic Tools': { placeholder: 'e.g., Initiate 12-week mindfulness program' },
  'Medications / Supplements': { placeholder: 'Start / stop / continue / adjust' },
  'Referrals or escalation needs': { placeholder: 'e.g., primary care, safety team, higher level of care' },
})

export const INTAKE_NOTE_COACHING_SECTIONS: Section[] = [{ fields: [text('Intake notes', 'notes')] }]
